import React, { useState, useEffect, useContext } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import EmbedAuthContext from '../../contexts/EmbedAuthContext';
import { submitTurnFeedback, getLocalFeedback, saveLocalFeedback } from '../../services/feedbackService';
import TurnFeedbackModal from './TurnFeedbackModal';
import CopyableText from '../common/CopyableText';
import { shouldShowDevControls } from '../../utils/embedGating';
import styles from './TurnFeedback.module.css';

interface ThumbsUpIconProps {
  filled: boolean;
}

interface ThumbsDownIconProps {
  filled: boolean;
}

interface TurnFeedbackProps {
  sessionId: string;
  turnNumber: number;
  initialFeedback?: number | null;
  initialFeedbackText?: string;
  feedbackId?: string;
  // TRAC-518 Phase 8a follow-up: Langfuse trace ID threaded from the turn's
  // response_complete event. Passing it to the backend lets the feedback
  // endpoint skip its DDB read (the last us-west-2-broken path).
  traceId?: string;
  // TRAC-631: Langfuse deep-link URL for the per-message trace. Present
  // ONLY for internal users (server-side wire-omitted otherwise). Drives
  // the copy-trace-link icon button rendered next to the feedback thumbs.
  langfuseTraceUrl?: string;
}

/**
 * Thumbs Up Icon
 */
const ThumbsUpIcon: React.FC<ThumbsUpIconProps> = ({ filled }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill={filled ? 'currentColor' : 'none'}
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
  </svg>
);

/**
 * Thumbs Down Icon
 */
const ThumbsDownIcon: React.FC<ThumbsDownIconProps> = ({ filled }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill={filled ? 'currentColor' : 'none'}
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17" />
  </svg>
);

/**
 * TurnFeedback - Inline feedback component for agent messages
 *
 * Flow:
 * 1. User clicks thumbs up or thumbs down
 * 2. Modal opens to optionally add a comment
 * 3. Both score and comment are submitted together
 * 4. Feedback is immutable - cannot be changed after submission
 */
const TurnFeedback: React.FC<TurnFeedbackProps> = ({ sessionId, turnNumber, initialFeedback, initialFeedbackText, traceId, langfuseTraceUrl }) => {
  const { getToken } = useAuth();
  const embedAuth = useContext(EmbedAuthContext);

  // In embed mode, use partner token instead of Cognito JWT (TRAC-231)
  const getEffectiveToken = async (): Promise<string | undefined> => {
    if (embedAuth?.credential) {
      return embedAuth.credential;
    }
    return getToken();
  };
  const [feedback, setFeedback] = useState<number | null>(initialFeedback ?? null);
  const [feedbackText, setFeedbackText] = useState<string>(initialFeedbackText ?? '');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [pendingFeedback, setPendingFeedback] = useState<number | null>(null);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  // Delay showing thumbs by 2 seconds after mount (gives streaming time to visually finish)
  const [isVisible, setIsVisible] = useState<boolean>(
    // If feedback already exists (page reload), show immediately
    initialFeedback !== null && initialFeedback !== undefined
  );
  useEffect(() => {
    if (isVisible) return;
    const timer = setTimeout(() => setIsVisible(true), 2000);
    return () => clearTimeout(timer);
  }, [isVisible]);

  // Check if feedback was already submitted (from server or localStorage)
  useEffect(() => {
    if (sessionId && turnNumber !== undefined) {
      // If initialFeedback exists, it means feedback was already submitted
      if (initialFeedback !== null && initialFeedback !== undefined) {
        setFeedback(initialFeedback);
        setFeedbackText(initialFeedbackText ?? '');
        setIsSubmitted(true);
      } else {
        // Check localStorage for previously submitted feedback
        const localData = getLocalFeedback(sessionId, turnNumber);
        if (localData && localData.feedback !== null && localData.feedback !== undefined) {
          setFeedback(localData.feedback);
          setFeedbackText(localData.feedbackText || '');
          setIsSubmitted(true);
        }
      }
    }
  }, [sessionId, turnNumber, initialFeedback, initialFeedbackText]);

  /**
   * Handle feedback button click - opens modal for optional comment
   */
  const handleFeedbackClick = (newFeedback: number): void => {
    // If already submitted, do nothing (immutable)
    if (isSubmitted || isSubmitting) return;

    // Store the pending feedback and open modal
    setPendingFeedback(newFeedback);
    setIsModalOpen(true);
  };

  /**
   * Handle feedback submission from modal (score + optional comment)
   */
  const handleFeedbackSubmit = async (comment: string): Promise<void> => {
    if (pendingFeedback === null || isSubmitted) return;

    setIsSubmitting(true);
    try {
      await submitTurnFeedback(sessionId, turnNumber, pendingFeedback, comment, getEffectiveToken, traceId);

      // Update state
      setFeedback(pendingFeedback);
      setFeedbackText(comment);
      setIsSubmitted(true);

      // Save to localStorage
      saveLocalFeedback(sessionId, turnNumber, pendingFeedback, comment);

      toast.success('Thank you for your feedback!');
    } catch (error: unknown) {
      // Check if it's an immutable feedback error
      if (error instanceof Error && error.message.includes('already submitted')) {
        setIsSubmitted(true);
        toast.error('Feedback was already submitted');
      } else {
        toast.error('Failed to submit feedback');
      }
    } finally {
      setIsSubmitting(false);
      setIsModalOpen(false);
      setPendingFeedback(null);
    }
  };

  /**
   * Handle modal close without submitting
   */
  const handleModalClose = (): void => {
    setIsModalOpen(false);
    setPendingFeedback(null);
  };

  if (!isVisible) return null;

  return (
    <>
      <div className={styles.feedbackContainer}>
        <button
          className={`${styles.feedbackButton} ${feedback === 1 ? styles.positive : ''} ${isSubmitted ? styles.submitted : ''}`}
          onClick={() => handleFeedbackClick(1)}
          disabled={isSubmitting || isSubmitted}
          title={isSubmitted ? 'Feedback submitted' : 'Helpful response'}
          aria-label={isSubmitted ? 'Feedback submitted' : 'Mark as helpful'}
        >
          <ThumbsUpIcon filled={feedback === 1} />
        </button>

        <button
          className={`${styles.feedbackButton} ${feedback === -1 ? styles.negative : ''} ${isSubmitted ? styles.submitted : ''}`}
          onClick={() => handleFeedbackClick(-1)}
          disabled={isSubmitting || isSubmitted}
          title={isSubmitted ? 'Feedback submitted' : 'Response not helpful'}
          aria-label={isSubmitted ? 'Feedback submitted' : 'Mark as not helpful'}
        >
          <ThumbsDownIcon filled={feedback === -1} />
        </button>

        {/* TRAC-631: Copy Langfuse trace deep-link + open in new tab.
            Gated identically to Dev Settings / Clear conversation buttons
            (see ChatInterface.tsx:3575) — visible everywhere except
            cross-origin prod customer embeds. */}
        {shouldShowDevControls(import.meta.env.VITE_ENV || 'dev') && (
          <>
            <CopyableText
              iconOnly
              text="Copy trace link"
              copyValue={langfuseTraceUrl}
            />
            <a
              className={styles.feedbackButton}
              href={langfuseTraceUrl || '#'}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                if (!langfuseTraceUrl) e.preventDefault();
              }}
              title="Open trace in Langfuse"
              aria-label="Open trace in Langfuse"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                <polyline points="15 3 21 3 21 9"></polyline>
                <line x1="10" y1="14" x2="21" y2="3"></line>
              </svg>
            </a>
          </>
        )}
      </div>

      <TurnFeedbackModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSubmit={handleFeedbackSubmit}
        feedbackType={pendingFeedback}
        sessionId={sessionId}
        turnNumber={turnNumber}
        isSubmitting={isSubmitting}
      />
    </>
  );
};

export default TurnFeedback;
